// Storage and synchronization manager for Custom Title Screen Artwork with Multi-Tier Persistence & Auto-Healing
import {
  saveArtworkToFirestore,
  deleteArtworkFromFirestore,
  subscribeFirestoreArtwork,
} from '../lib/firebase';

// Versioned keys ensuring complete reset when requested by user
const RESET_PURGE_VERSION_KEY = 'tamazight_title_artwork_purged_v4';
const LOCAL_STORAGE_KEY = 'tamazight_custom_title_artwork_v4';
const RAW_DATA_STORAGE_KEY = 'tamazight_custom_title_artwork_raw_v4';
const IDB_NAME = 'tamazight_title_artwork_db';
const IDB_STORE = 'artwork_store';
const IDB_KEY_RAW = 'raw_artwork_data_url';

export const DEFAULT_TITLE_ARTWORK = '/custom/title-artwork.jpg';

let currentTitleArtwork: string = DEFAULT_TITLE_ARTWORK;
let currentRawDataUrl: string | null = null;
let hasReceivedFirestoreSnapshot = false;
const listeners = new Set<(url: string | null) => void>();
let preloadedImageInstance: HTMLImageElement | null = null;
let isServerSyncing = false;

// ==========================================
// IndexedDB Persistent Storage Helpers
// ==========================================
function getIndexedDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !window.indexedDB) {
      return reject(new Error('IndexedDB not supported'));
    }
    const request = indexedDB.open(IDB_NAME, 3);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function getStoredArtworkFromIDB(): Promise<string | null> {
  try {
    const db = await getIndexedDB();
    return new Promise((resolve) => {
      const tx = db.transaction(IDB_STORE, 'readonly');
      const store = tx.objectStore(IDB_STORE);
      const req = store.get(IDB_KEY_RAW);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
    });
  } catch (e) {
    return null;
  }
}

export async function setStoredArtworkInIDB(dataUrl: string): Promise<void> {
  try {
    const db = await getIndexedDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      const store = tx.objectStore(IDB_STORE);
      const req = store.put(dataUrl, IDB_KEY_RAW);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch (e) {
    console.warn('Could not store artwork in IndexedDB:', e);
  }
}

export async function clearStoredArtworkFromIDB(): Promise<void> {
  try {
    const db = await getIndexedDB();
    return new Promise((resolve) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      const store = tx.objectStore(IDB_STORE);
      const req = store.clear();
      req.onsuccess = () => resolve();
      req.onerror = () => resolve();
    });
  } catch (e) {
    console.warn('Could not clear artwork from IndexedDB:', e);
  }
}

export function preloadArtwork(url: string | null): void {
  if (!url || typeof window === 'undefined') return;
  try {
    const img = new Image();
    img.src = url;
    preloadedImageInstance = img;
  } catch (e) {
    // Ignore
  }
}

// 1. One-time auto-purge check: clean legacy storage
if (typeof window !== 'undefined') {
  try {
    preloadArtwork(DEFAULT_TITLE_ARTWORK);
    const rawSaved = localStorage.getItem(RAW_DATA_STORAGE_KEY);
    const cachedRef = localStorage.getItem(LOCAL_STORAGE_KEY);

    if (rawSaved && rawSaved.startsWith('data:image')) {
      currentRawDataUrl = rawSaved;
      currentTitleArtwork = rawSaved;
      preloadArtwork(rawSaved);
    } else if (cachedRef && !cachedRef.endsWith('.webp')) {
      currentTitleArtwork = cachedRef;
      preloadArtwork(cachedRef);
    } else {
      currentTitleArtwork = DEFAULT_TITLE_ARTWORK;
    }
  } catch (e) {
    console.warn('Error reading cached title artwork:', e);
  }

  // Check IndexedDB on boot
  getStoredArtworkFromIDB().then((idbArtwork) => {
    if (idbArtwork && idbArtwork.startsWith('data:image')) {
      currentRawDataUrl = idbArtwork;
      if (!currentTitleArtwork || currentTitleArtwork === DEFAULT_TITLE_ARTWORK) {
        currentTitleArtwork = idbArtwork;
        preloadArtwork(idbArtwork);
        notifyListeners(idbArtwork);
      }
    }
  });
}

/**
 * Get any raw base64 data URL stored locally (in-memory, localStorage, or IndexedDB)
 */
export async function getStoredRawArtwork(): Promise<string | null> {
  if (currentRawDataUrl && currentRawDataUrl.startsWith('data:image')) {
    return currentRawDataUrl;
  }
  if (typeof window !== 'undefined') {
    try {
      const raw = localStorage.getItem(RAW_DATA_STORAGE_KEY);
      if (raw && raw.startsWith('data:image')) {
        currentRawDataUrl = raw;
        return raw;
      }
    } catch {}
  }
  const idb = await getStoredArtworkFromIDB();
  if (idb && idb.startsWith('data:image')) {
    currentRawDataUrl = idb;
    return idb;
  }
  return null;
}

/**
 * Fetch from server & auto-heal if server container restarted and lost the runtime file.
 * Seamlessly hydrates IndexedDB & localStorage across different browsers and devices.
 */
export async function syncTitleArtworkFromServer(): Promise<string | null> {
  if (isServerSyncing) return currentTitleArtwork;
  isServerSyncing = true;
  try {
    const res = await fetch(`/api/title-artwork?t=${Date.now()}`, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
      },
    });

    if (res.ok) {
      const data = await res.json();
      const serverUrl: string | null = data.artworkUrl || null;
      const serverDataUrl: string | null = data.imageDataUrl || null;

      // Only adopt server image if we haven't already received a custom artwork from Firestore/IndexedDB
      if ((serverUrl || serverDataUrl) && (!currentTitleArtwork || currentTitleArtwork === DEFAULT_TITLE_ARTWORK)) {
        const preferredArtwork = serverDataUrl || serverUrl!;
        currentTitleArtwork = preferredArtwork;

        if (serverDataUrl) {
          currentRawDataUrl = serverDataUrl;
          try {
            localStorage.setItem(RAW_DATA_STORAGE_KEY, serverDataUrl);
          } catch {}
          setStoredArtworkInIDB(serverDataUrl);
        }

        if (serverUrl) {
          try {
            localStorage.setItem(LOCAL_STORAGE_KEY, serverUrl);
          } catch {}
        }

        preloadArtwork(preferredArtwork);
        notifyListeners(preferredArtwork);
        return preferredArtwork;
      }
    }
  } catch (err) {
    console.warn('Failed to sync title artwork from server:', err);
  } finally {
    isServerSyncing = false;
  }
  return currentTitleArtwork;
}

/**
 * Save/Upload title artwork to server AND local persistent storage
 */
export async function saveTitleArtwork(imageDataUrl: string): Promise<{ success: boolean; url?: string; error?: string }> {
  try {
    currentRawDataUrl = imageDataUrl;
    currentTitleArtwork = imageDataUrl;

    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(RAW_DATA_STORAGE_KEY, imageDataUrl);
      } catch (e) {
        console.warn('Could not save raw artwork to localStorage:', e);
      }
    }
    await setStoredArtworkInIDB(imageDataUrl);

    preloadArtwork(imageDataUrl);
    notifyListeners(imageDataUrl);

    // Save to Firestore first for cloud permanence
    try {
      await saveArtworkToFirestore(imageDataUrl);
    } catch (e) {
      console.warn('Could not save artwork to Firestore:', e);
    }

    // Save to Express server for static caching
    try {
      const response = await fetch('/api/title-artwork', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageDataUrl }),
      });
      if (response.ok) {
        const data = await response.json();
        if (data.artworkUrl) {
          try {
            localStorage.setItem(LOCAL_STORAGE_KEY, data.artworkUrl);
          } catch {}
        }
      }
    } catch (e) {
      console.warn('Could not save artwork to local server file:', e);
    }

    return { success: true, url: imageDataUrl };
  } catch (err: any) {
    console.error('Error saving title artwork:', err);
    return { success: true, url: imageDataUrl };
  }
}

/**
 * Delete custom title artwork completely (server + local + Firestore)
 */
export async function deleteTitleArtwork(): Promise<boolean> {
  try {
    currentTitleArtwork = DEFAULT_TITLE_ARTWORK;
    currentRawDataUrl = null;
    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem(LOCAL_STORAGE_KEY);
        localStorage.removeItem(RAW_DATA_STORAGE_KEY);
        localStorage.removeItem('tamazight_custom_title_artwork');
        localStorage.removeItem('tamazight_custom_title_artwork_raw_v1');
      } catch {}
    }
    await clearStoredArtworkFromIDB();
    notifyListeners(DEFAULT_TITLE_ARTWORK);

    // Delete from Firestore
    try {
      await deleteArtworkFromFirestore();
    } catch (e) {
      console.warn('Could not delete artwork from Firestore:', e);
    }

    // Delete from server
    try {
      await fetch('/api/title-artwork', { method: 'DELETE' });
    } catch (e) {
      console.warn('Could not delete artwork from server:', e);
    }

    return true;
  } catch (err) {
    console.error('Error deleting title artwork:', err);
    return false;
  }
}

export function getTitleArtwork(): string {
  return currentTitleArtwork || DEFAULT_TITLE_ARTWORK;
}

export function subscribeTitleArtwork(callback: (url: string) => void): () => void {
  listeners.add(callback);
  callback(currentTitleArtwork || DEFAULT_TITLE_ARTWORK);
  return () => {
    listeners.delete(callback);
  };
}

function notifyListeners(url: string | null) {
  const finalUrl = url || DEFAULT_TITLE_ARTWORK;
  listeners.forEach((cb) => {
    try {
      cb(finalUrl);
    } catch (e) {
      console.error('Error in title artwork listener:', e);
    }
  });
}

// Automatically sync on initial module load
if (typeof window !== 'undefined') {
  // 1. Check local server once
  syncTitleArtworkFromServer();

  // 2. Subscribe to real-time Firestore artwork updates (Authoritative cloud source)
  try {
    subscribeFirestoreArtwork((cloudDataUrl) => {
      hasReceivedFirestoreSnapshot = true;
      if (cloudDataUrl && cloudDataUrl.startsWith('data:image')) {
        if (cloudDataUrl !== currentTitleArtwork) {
          currentTitleArtwork = cloudDataUrl;
          currentRawDataUrl = cloudDataUrl;
          try {
            localStorage.setItem(RAW_DATA_STORAGE_KEY, cloudDataUrl);
          } catch {}
          setStoredArtworkInIDB(cloudDataUrl);
          preloadArtwork(cloudDataUrl);
          notifyListeners(cloudDataUrl);

          // Synchronize to express server in background so server file matches Firestore
          fetch('/api/title-artwork', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ imageDataUrl: cloudDataUrl }),
          }).catch(() => {});
        }
      } else if (!cloudDataUrl) {
        // Firestore has no custom artwork
        if (currentTitleArtwork && currentTitleArtwork.startsWith('data:image')) {
          // If we had a custom image that was removed in cloud, reset to default
          currentTitleArtwork = DEFAULT_TITLE_ARTWORK;
          currentRawDataUrl = null;
          try {
            localStorage.removeItem(RAW_DATA_STORAGE_KEY);
            localStorage.removeItem(LOCAL_STORAGE_KEY);
          } catch {}
          clearStoredArtworkFromIDB();
          notifyListeners(DEFAULT_TITLE_ARTWORK);
        }
      }
    });
  } catch (err) {
    console.warn('Could not subscribe to Firestore artwork updates:', err);
  }
}
